import UArray from "./UArray.mts";
import UString from "./UString.mts";

type Interval = [Interval.Bound, Interval.Bound];

namespace Interval {
  export type Bound = { inclusive?: boolean; value: number };

  export function includes(
    interval: Interval | Interval.Notation,
    value: number
  ) {
    const resolved =
      typeof interval === "string" ? Notation.parse(interval) : interval;

    if (value > resolved[0].value && value < resolved[1].value) return true;
    if (resolved[0].inclusive && value === resolved[0].value) return true;
    if (resolved[1].inclusive && value === resolved[1].value) return true;
    return false;
  }

  export type Notation =
    | Notation.Full
    // A partial interval means the other bound is Infinity
    | Notation.Left
    | Notation.Right
    | Notation.Single;

  export namespace Notation {
    export type Full = `${Left},${Right}`;
    export type Left = `${Open}${number}`;
    export type Right = `${number}${Close}`;
    export type Single =
      `${typeof delimiter.open.inclusive}${number}${typeof delimiter.close.inclusive}`;
    export type Open = (typeof delimiter.open)[number];
    export type Close = (typeof delimiter.close)[number];
    export const delimiter = (() => {
      const open = {
        inclusive: "[",
        exclusive: "(",
      } as const;
      const close = {
        inclusive: "]",
        exclusive: ")",
      } as const;
      return {
        open: Object.assign([open.inclusive, open.exclusive] as const, open),
        close: Object.assign(
          [close.inclusive, close.exclusive] as const,
          close
        ),
      } as const;
    })();

    export function normalize(notation: Notation): Full {
      notation = notation.replace(/\s/g, "") as Notation;
      if (!UArray.includesLiteral(Notation.delimiter.open, notation[0]))
        return `(-Infinity,${notation}` as Full;
      if (!UArray.includesLiteral(Notation.delimiter.close, notation.at(-1)))
        return `${notation},Infinity)` as Full;
      if (!notation.includes(",")) {
        const value = notation.slice(1, -1);
        return `${notation[0]}${value},${value}${notation.at(-1)}` as Full;
      }
      return notation as Full;
    }

    export function parse(notation: Notation): Interval {
      const full = normalize(notation);
      const [left, right] = UString.split(full, ",");
      return [
        {
          inclusive: left[0] === Notation.delimiter.open.inclusive,
          value: +left.slice(1),
        },
        {
          inclusive: right.at(-1) === Notation.delimiter.close.inclusive,
          value: +right.slice(0, -1),
        },
      ];
    }
  }

  export type Map<T> = Record<Interval.Notation, T>;
  export namespace Map {
    export function createLookup<T>(map: Map<T>) {
      const entries = Object.entries(map).map(
        ([key, value]) => [Notation.parse(key), value] as const
      );

      return (n: number) => {
        for (const [interval, value] of entries) {
          if (includes(interval, n)) return value;
        }
      };
    }
  }
}

export default Interval;
